// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import http from '@/lib/http'

import HomeView from '@/views/HomeView.vue'
import DashboardLoginView from '@/views/DashboardLoginView.vue'
import DashboardUserRegisterView from '@/views/DashboardUserRegisterView.vue'
import AdminCreateAccountView from '@/views/AdminCreateAccountView.vue'
import AdminDashboardLayout from '@/layouts/AdminDashboardLayout.vue'

const adminChild = (path, name, title, loader) => ({
  path,
  name,
  component: loader,
  meta: { title, requiresAuth: true, role: 'admin' }
})

const staffChild = (path, name, title, loader) => ({
  path,
  name,
  component: loader,
  meta: { title, requiresAuth: true, role: 'staff' }
})

const routes = [
  { path: '/', redirect: 'home' },

  { path: '/home', name: 'home', component: HomeView },

  { path: '/privacy-policy', name: 'privacy-policy', component: () => import('@/views/PrivacyPolicyView.vue') },

  { path: '/terms-of-service', name: 'terms-of-service', component: () => import('@/views/TermsOfServiceView.vue') },

  { path: '/sms-terms', name: 'sms-terms', component: () => import('@/views/SmsTermsView.vue') },

  { path: '/book', name: 'book', component: () => import('@/views/BookView.vue') },

  { path: '/login', name: 'login', component: DashboardLoginView },

{ path: '/register', redirect: '/login', name: 'register', component: DashboardUserRegisterView },

  {
    path: '/admin/create',
    name: 'admin-create',
    component: AdminCreateAccountView,
    meta: { requiresAuth: true, role: 'admin', requiresHasAccount: false }
  },

  {
    path: '/manage-booking/:token',
    name: 'ManageBooking',
    component: () => import('@/views/ManageBookingView.vue'),
    props: true
  },

  {
    path: '/error',
    name: '404',
    component: () => import('@/views/404Generic.vue'),
    props: true
  },

  {
    path: '/widget/book/:bookingAddress',
    name: 'BookWidget',
    component: () => import('@/views/widget/BookWidgetView.vue')
  },

  {
    path: '/widget/org/:orgKey',
    name: 'OrgWidget',
    component: () => import('@/views/widget/OrgWidgetView.vue')
  },

  {
    path: '/admin',
    name: 'dashboard',
    component: AdminDashboardLayout,
    meta: { requiresAuth: true, role: 'admin', requiresHasAccount: true },
    redirect: { name: 'admin-dashboard' },
    children: [
      adminChild(
        'dashboard',
        'admin-dashboard',
        'Overview',
        () => import('@/views/AdminDashboardView.vue')
      ),
      adminChild(
        'staff',
        'admin-staff',
        'Staff',
        () => import('@/views/admin/StaffPage.vue')
      ),
      adminChild(
        'services',
        'admin-services',
        'Services',
        () => import('@/views/admin/ServicesPage.vue')
      ),
      adminChild(
        'hours',
        'admin-hours',
        'Opening Hours',
        () => import('@/views/admin/HoursPage.vue')
      ),
      adminChild(
        'customers',
        'admin-customers',
        'Customers',
        () => import('@/views/admin/CustomersPage.vue')
      ),
      adminChild(
        'calendar',
        'admin-calendar',
        'Calendar',
        () => import('@/views/admin/CalendarPage.vue')
      ),
      adminChild(
        'bookings',
        'admin-bookings',
        'Bookings',
        () => import('@/views/admin/BookingsPage.vue')
      ),
      adminChild(
        'analytics',
        'admin-analytics',
        'Analytics',
        () => import('@/views/admin/AnalyticsPage.vue')
      ),

      adminChild(
        'config/merchant',
        'admin-config-merchant',
        'Merchant Configuration',
        () => import('@/views/admin/MerchantConfigPage.vue')
      ),
      adminChild(
        'config/payment',
        'admin-config-payment',
        'Payment Configuration',
        () => import('@/views/admin/PaymentConfigPage.vue')
      ),
      adminChild(
        'config/bookingconfig',
        'admin-config-bookingconfig',
        'Booking Configuration',
        () => import('@/views/admin/BookingConfigPage.vue')
      ),
      adminChild(
        'config/widget',
        'admin-config-widget',
        'Widget Configuration',
        () => import('@/views/admin/WidgetConfigPage.vue')
      ),
    ]
  },

  {
    path: '/staff',
    name: 'staffdashboard',
    component: AdminDashboardLayout,
    meta: { requiresAuth: true, role: 'staff' },
    redirect: { name: 'staff-dashboard' },
    children: [
      staffChild(
        'dashboard',
        'staff-dashboard',
        'Overview',
        () => import('@/views/StaffDashboardView.vue')
      ),
      staffChild(
        'staff',
        'staff-staff',
        'Staff',
        () => import('@/views/admin/StaffPage.vue')
      ),
      staffChild(
        'services',
        'staff-services',
        'Services',
        () => import('@/views/admin/ServicesPage.vue')
      ),
      staffChild(
        'customers',
        'staff-customers',
        'Customers',
        () => import('@/views/admin/CustomersPage.vue')
      ),
      staffChild(
        'calendar',
        'staff-calendar',
        'Calendar',
        () => import('@/views/admin/CalendarPage.vue')
      ),
      staffChild(
        'bookings',
        'staff-bookings',
        'Bookings',
        () => import('@/views/admin/BookingsPage.vue')
      ),
    ]
  },

  {
    path: '/superadmin',
    name: 'superadmin',
    component: () => import('@/views/SuperAdminDashboardView.vue'),
    meta: { requiresAuth: true, role: 'superadmin' }
  },

  // 404
  { path: '/:pathMatch(.*)*', redirect: '/home' }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0, behavior: 'smooth' }
  }
})

router.beforeEach(async (to, from, next) => {
  const session = useSessionStore()
  if (!session.token) session.loadFromStorage?.()

  const isAuthed = !!session.token
  const requiresAuth = !!to.meta?.requiresAuth
  const requiredRole = to.meta?.role
  const requiresHasAccount = !!to.meta?.requiresHasAccount

  if (to.name === 'admin-create' && isAuthed && session.role === 'admin' && session.hasAccount) {
    return next({ name: 'dashboard' })
  }

  if (to.name === 'login' && isAuthed && session.role === 'staff') {
    return next({ name: 'staffdashboard' })
  }

  if (requiresAuth && !isAuthed) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  if (requiredRole && session.role !== requiredRole) {
    return next({ name: 'home' })
  }

  if (requiresHasAccount && !session.hasAccount) {
    return next({ name: 'admin-create' })
  }

  if (to.name === 'login' && isAuthed) {
    return next({ name: session.role === 'superadmin' ? 'superadmin' : 'admin-dashboard' })
  }

  // Gate the public manage booking page by verifying the manage token with the backend
  if (to.name === 'ManageBooking') {
    const token = to.params?.token
    const businessId = to.query?.businessId
    if (!token || !businessId) {
      return next({ name: '404', query: { message: 'Invalid booking link.' } })
    }
    try {
      const resp = await http.get(`/bookings/manage/${token}`, { params: { businessId } })
      if (!resp?.data?.booking) {
        return next({ name: '404', query: { message: 'Booking not found or link expired.' } })
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Booking link is invalid or expired.'
      return next({ name: '404', query: { message: msg } })
    }
  }

  next()
})

export default router
